import { useMemo } from 'react';
import { ThreadStatusResult } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { ThreadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { InboxLocationPhones, InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { PhoneOptionsProps } from './phone-options';

export type ThreadStatusResultLike = Pick<ThreadStatusResult, 'threadId' | 'status' | 'locationId' | 'locationPhone'>;

type ThreadStatusResultInput = Omit<ThreadStatusResultLike, 'locationPhone'> & { locationPhone?: string };

type FormattedStatusDataByLocation = {
  activeConversationsByLocation: Record<string, ThreadStatusResultLike[]>;
  activeConversationsCountByLocation: number;
  allConversationsByLocation: Record<string, ThreadStatusResultLike[]>;
  allConversationsCountByLocation: number;
};

type UseBatchGetThreadStatusQueryArgs = {
  personPhone?: string;
  groupParticipants?: string[];
  isGroup?: boolean;
};

const getStatusDataByLocation = (threadStatuses: ThreadStatusResultInput[]): FormattedStatusDataByLocation =>
  threadStatuses.reduce<FormattedStatusDataByLocation>(
    (acc, curr) => {
      if (!curr.locationPhone) {
        return acc;
      }

      const normalizedThreadStatus: ThreadStatusResultLike = {
        ...curr,
        locationPhone: curr.locationPhone,
      };

      // Deduplicate by locationPhone within each location to prevent duplicate entries
      const allEntries = acc.allConversationsByLocation[curr.locationId] || [];
      const isDuplicateAll = allEntries.some((entry) => entry.locationPhone === curr.locationPhone);
      if (!isDuplicateAll) {
        acc.allConversationsByLocation[curr.locationId] = [...allEntries, normalizedThreadStatus];
        acc.allConversationsCountByLocation += 1;
      }

      // active conversations logic
      if (curr.status === ThreadStatus.ACTIVE) {
        const existingEntries = acc.activeConversationsByLocation[curr.locationId] || [];
        const isDuplicateActive = existingEntries.some((entry) => entry.locationPhone === curr.locationPhone);
        if (!isDuplicateActive) {
          acc.activeConversationsByLocation[curr.locationId] = [...existingEntries, normalizedThreadStatus];
          acc.activeConversationsCountByLocation += 1;
        }
      }

      return acc;
    },
    {
      activeConversationsCountByLocation: 0,
      allConversationsCountByLocation: 0,
      activeConversationsByLocation: {},
      allConversationsByLocation: {},
    }
  );

export const useBatchGetThreadStatusQuery = ({
  personPhone,
  groupParticipants = [],
  isGroup = false,
}: UseBatchGetThreadStatusQueryArgs) => {
  const locationPhonesQuery = InboxLocationPhones.Hooks.useLocationPhones();

  const { locationPhonesByLocation, filteredLocationPhonesByLocation } = useMemo<{
    locationPhonesByLocation: Record<string, string[]>;
    filteredLocationPhonesByLocation: Record<string, InboxLocationPhonesTypes.LocationPhone[]>;
  }>(() => {
    const newFilteredLocationPhonesByLocation: Record<string, InboxLocationPhonesTypes.LocationPhone[]> = {};
    const newLocationPhonesByLocation = Object.fromEntries(
      Object.entries(locationPhonesQuery.data.locationPhonesByLocation ?? {}).map(([locationId, entry]) => {
        const existingFilteredEntries = new Set(newFilteredLocationPhonesByLocation[locationId] || []);
        const result = [
          locationId,
          entry.map((e) => {
            existingFilteredEntries.add(e);
            return e.locationPhone;
          }),
        ];
        newFilteredLocationPhonesByLocation[locationId] = Array.from(existingFilteredEntries);
        return result;
      })
    );
    return {
      locationPhonesByLocation: newLocationPhonesByLocation,
      filteredLocationPhonesByLocation: newFilteredLocationPhonesByLocation,
    };
  }, [locationPhonesQuery.data.locationPhonesByLocation]);
  const personRequests = SMSDataV4.Utils.calculatePossibleThreadStatusRequests({
    personPhones: personPhone ? [personPhone] : [],
    locationPhonesByLocation,
  });
  const groupRequests = SMSDataV4.Utils.calculatePossibleGroupThreadStatusRequests({
    personPhones: groupParticipants,
    locationPhonesByLocation,
  });

  // For group flow with single participant, we should still check their person thread status
  const shouldUsePersonQuery = !isGroup || (isGroup && groupParticipants.length < 2);
  const personQueryEnabled = shouldUsePersonQuery && !!personPhone;
  const groupQueryEnabled = isGroup && groupParticipants.length >= 2;

  const personThreadStatusQuery = SMSDataV4.Queries.useBatchGetThreadStatusQuery(
    {
      requests: personRequests,
    },
    {
      enabled: personQueryEnabled,
      select: (data) => getStatusDataByLocation(data.threadStatuses),
    }
  );

  const groupThreadStatusQuery = SMSDataV4.Queries.useBatchGetGroupThreadStatusQuery(
    {
      requests: groupRequests,
    },
    {
      enabled: groupQueryEnabled,
      select: (data) => getStatusDataByLocation(data.threadStatuses),
    }
  );

  // Use group query only when we have 2+ participants, otherwise use person query
  const threadStatusQuery = groupQueryEnabled ? groupThreadStatusQuery : personThreadStatusQuery;

  const activeConversationCount = threadStatusQuery.data?.activeConversationsCountByLocation ?? 0;
  const hasActiveConversation = activeConversationCount > 0;

  const activeConversationsByLocation = useMemo(
    () => threadStatusQuery.data?.activeConversationsByLocation ?? {},
    [threadStatusQuery]
  );

  // Flat array of all active conversation phone numbers (for visibility logic)
  const activeConversationPhones = useMemo(
    () =>
      Object.values(activeConversationsByLocation)
        .flat()
        .map((conv) => conv.locationPhone),
    [activeConversationsByLocation]
  );

  const allConversationsByLocation = useMemo(
    () => threadStatusQuery.data?.allConversationsByLocation ?? {},
    [threadStatusQuery]
  );

  const availableConversationsNumbersByLocation = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(allConversationsByLocation).map(([locationId, threadStatuses]) => [
          locationId,
          // filter out NEW or UNSPECIFIED statuses unless they have an enabled outbound number
          threadStatuses.filter(
            (ts) =>
              ts.status === ThreadStatus.ACTIVE ||
              ts.status === ThreadStatus.ARCHIVED ||
              locationPhonesQuery.enabledData.allLocationPhones.find(
                (locationData) => locationData.locationPhone === ts.locationPhone
              )
          ),
        ])
      ),
    [allConversationsByLocation, locationPhonesQuery.enabledData.allLocationPhones]
  );

  const allPhoneOptionsByLocation = useMemo(
    (): Record<string, PhoneOptionsProps['phones']> =>
      Object.fromEntries(
        Object.entries(allConversationsByLocation).map(([locationId, threadStatuses]) => [
          locationId,
          threadStatuses.map((ts) => {
            const matchingLocationPhone = locationPhonesQuery.data.locationPhonesByLocation?.[locationId]?.find(
              (lp) => lp.locationPhone === ts.locationPhone
            );
            return {
              ...ts,
              displayName: matchingLocationPhone?.displayName || undefined,
              defaultSms: matchingLocationPhone?.defaultSms,
            };
          }),
        ])
      ),
    [allConversationsByLocation, locationPhonesQuery.data.locationPhonesByLocation]
  );

  // Determine if we're loading thread status data
  const isThreadStatusLoading =
    (personQueryEnabled && personThreadStatusQuery.isLoading) ||
    (groupQueryEnabled && groupThreadStatusQuery.isLoading);

  return {
    activeConversationCount,
    activeConversationsByLocation,
    activeConversationPhones,
    allPhoneOptionsByLocation,
    availableConversationsNumbersByLocation,
    filteredLocationPhonesByLocation,
    hasActiveConversation,
    locationPhonesQuery,
    isThreadStatusLoading,
  };
};
